import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "wedding-shower-gift-exchanges",
  "title": "Wedding and Bridal Shower Gift Strategies",
  "excerpt": "Navigate wedding season with confidence. From registry shopping to group gifts, make your wedding gift memorable and appropriate.",
  "date": "2025-06-01",
  "readTime": "7 min read",
  "category": "Festivals",
  "image": "💍",
  "heroImage": "https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Wedding and Bridal Shower Gift Strategies\n\nWedding gifts require thought about budget, timing, and appropriateness. Here's your complete guide.\n\n## Registry vs. Off-Registry\n\n**Registry Benefits**:\n- You know they want it\n- No duplicates\n- Easy for you\n- Organized delivery\n\n**When to Go Off-Registry**:\n- You're very close to the couple\n- You have a unique, meaningful idea\n- Registry is very limited\n- You want something personal\n\n## Budget Guidelines\n\n**Relationship-Based**:\n- Acquaintance/Coworker: $50-75\n- Friend: $75-150\n- Close Friend: $150-200\n- Family: $200-500+\n- Destination Wedding: Lower amount acceptable (you're spending on travel)\n\n**Factors to Adjust**:\n- Your financial situation\n- Wedding location/expenses\n- Local cost of living\n- Multiple wedding events\n\n## Bridal Shower Gifts\n\n**Typical Budget**: $25-75\n\n**Popular Ideas**:\n- Kitchen items from registry\n- Spa or pampering products\n- Lingerie or sleepwear\n- Date night basket\n- Recipe box with favorite recipes\n- Personalized items\n\n**Group Shower Gifts**:\n- Big registry items (KitchenAid, Dyson)\n- Coordinate with other guests\n- $20-30 per person\n\n## Wedding Gift Ideas\n\n**Traditional Registry Items**:\n- Kitchen appliances\n- Cookware and bakeware\n- Bedding and linens\n- Dinnerware and glassware\n- Small appliances\n\n**Unique Off-Registry Gifts**:\n- Art for their home\n- Experience gifts (cooking class, wine tasting)\n- Subscription boxes\n- Custom items (cutting board, doormat)\n- Quality luggage for honeymoon\n\n**Monetary Gifts**:\n- Cash or check\n- Contribution to honeymoon fund\n- Gift cards to favorite stores\n- Stock or investment contributions\n\n## Timing and Delivery\n\n**Bridal Shower**:\n- Bring to shower\n- Or ship to host's home beforehand\n\n**Wedding**:\n- **DO NOT** bring to wedding (creates logistics issues)\n- Ship to couple's home before or after\n- You have up to 1 year traditionally (but sooner is better)\n- 2-3 weeks after wedding is ideal\n\n## Group Wedding Gifts\n\n**Organizing Tips**:\n- Start conversation early\n- Use SwapGifts to coordinate\n- Set per-person budget\n- Choose high-value registry item\n- Include all contributors' names on card\n\n**Appropriate Group Sizes**:\n- Office colleagues: $20-30 each\n- Friend group: $50-75 each\n- Extended family: $50-100 each\n\n## Destination Wedding Considerations\n\n**Adjusted Expectations**:\n- Guests spending on travel\n- Smaller gifts are acceptable\n- Or skip shower gift if attending wedding\n- Combine shower + wedding budget\n\n**Typical Budget**: $50-100 total\n\n## Multiple Events Dilemma\n\n**If Invited to Everything**:\n- Engagement party: Optional, small gift or card\n- Bridal shower: Yes, $50-75\n- Bachelorette: Split costs, maybe small gift\n- Wedding: Yes, $100-200\n\n**Budget Fatigue**:\n- It's OK to skip some events\n- Combine budgets\n- Be honest about finances\n\n## Special Situations\n\n**Second Weddings**:\n- Still give gifts\n- Often more practical than traditional\n- Lower budget acceptable\n\n**Elopements**:\n- Send gift to home\n- Similar budget to regular wedding\n- Celebrate however they request\n\n**Didn't Attend**:\n- Sending a gift is optional but nice\n- Lower budget appropriate\n- Card with well-wishes minimum\n\n## Gift Wrapping for Weddings\n\n**Bridal Shower**:\n- Pretty wrapping paper\n- Bows and ribbons\n- Bring in nice gift bag\n\n**Wedding**:\n- Ship directly from store\n- Or simple elegant wrapping if hand-delivering\n- Include gift receipt\n\n## What to Avoid\n\n- Super personal items (too intimate)\n- Used or regifted items\n- Cash in card at wedding reception\n- Pets or live animals\n- Anything not on registry unless very close\n\n## Making It Personal\n\n**Add Special Touches**:\n- Handwritten card with personal message\n- Share a favorite memory\n- Include well-wishes for future\n- Add family recipe with kitchen items\n- Photo of you together in frame\n\n## Conclusion\n\nWedding gifts celebrate the couple's new journey. Whether from the registry or a thoughtful surprise, your gift shows love and support for their marriage."
};

export default function WeddingShowerGiftExchangesPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
